import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { PageHeader, EmailStatusBanner } from "@/components/admin/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateTime } from "@/lib/format";

const STATUSES = ["New", "Unread", "Open", "Contacted", "Awaiting Response", "Referred to Agency", "Qualified", "Viewing Arranged", "Offer Made", "Converted", "Closed", "Unsuccessful", "Duplicate", "Spam", "Archived"];

export default function AdminEnquiries() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [emailConfigured, setEmailConfigured] = useState(true);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    const r = await api.get(`/admin/enquiries?${params.toString()}`);
    setItems(r.data);
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/admin/emails").then((r) => setEmailConfigured(r.data.email_configured)).catch(() => {}); }, []);

  return (
    <div>
      <PageHeader title="Enquiries" subtitle="Property enquiries submitted by visitors"
        action={<a href={`${API}/admin/enquiries/export`}><Button variant="outline" className="rounded-full gap-2" data-testid="export-enquiries"><Download className="w-4 h-4" /> Export CSV</Button></a>} />
      <EmailStatusBanner configured={emailConfigured} />

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, property..." className="pl-9" data-testid="enquiry-search" /></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="enquiry-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {items === null ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#C81D35] animate-spin" /></div>
          : items.length === 0 ? <div className="text-center py-16 text-muted-foreground" data-testid="enquiries-empty">No enquiries found.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="p-4 font-medium">Enquirer</th><th className="p-4 font-medium">Property</th><th className="p-4 font-medium">Status</th><th className="p-4 font-medium">Received</th></tr></thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30 cursor-pointer" data-testid={`enquiry-row-${e.id}`}>
                    <td className="p-4"><Link to={`/admin/enquiries/${e.id}`} className="block"><p className="font-medium text-[#111827]">{e.full_name} {!e.read && <span className="ml-1.5 text-[10px] bg-[#C81D35] text-white px-1.5 py-0.5 rounded-full align-middle">NEW</span>}</p><p className="text-xs text-muted-foreground">{e.email} · {e.phone}</p></Link></td>
                    <td className="p-4"><Link to={`/admin/enquiries/${e.id}`} className="text-[#111827] hover:text-[#C81D35]">{e.property_title || "General"}</Link></td>
                    <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-muted whitespace-nowrap">{e.status}</span></td>
                    <td className="p-4 text-muted-foreground whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
